<template>
    <BaseCard padding="sm" radius="lgr">
        <form class="flex items-center gap-2" @submit.prevent="handleSearch">
            <div class="min-w-0 flex-1">
                <BaseInput
                    v-model="searchInput"
                    type="search"
                    :aria-label="t('components.conversationSearch.ariaLabel')"
                    :placeholder="t('components.conversationSearch.placeholder')"
                    :disabled="isFetching"
                    maxlength="1000"
                    @keyup.escape="handleClear">
                    <template #prefix><i class="bi bi-search" aria-hidden="true"></i></template>
                </BaseInput>
            </div>
            <div v-if="hasSearched || searchInput" class="flex shrink-0 items-center gap-2">
                <BaseButton
                    v-if="hasSearched"
                    theme="tertiary"
                    size="sm"
                    icon-only
                    type="button"
                    :aria-label="t('components.conversationSearch.refresh')"
                    :title="t('components.conversationSearch.refreshTitle')"
                    :disabled="isFetching"
                    @click="handleRefresh">
                    <i class="bi bi-arrow-clockwise" aria-hidden="true"></i>
                </BaseButton>
                <BaseButton
                    v-if="searchInput"
                    theme="tertiary"
                    size="sm"
                    icon-only
                    type="submit"
                    :aria-label="t('components.conversationSearch.search')"
                    :title="t('components.conversationSearch.search')"
                    :disabled="isFetching">
                    <i class="bi bi-arrow-right-circle-fill text-lg" aria-hidden="true"></i>
                </BaseButton>
            </div>
        </form>
        <div class="flex flex-col gap-2 mt-3 md:flex-row md:items-start">
            <span v-if="visibleChips.length > 1" class="body-small text-ink-muted whitespace-nowrap md:pt-2">{{ t("components.conversationSearch.searchIn") }}</span>
            <div class="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                <!-- FIXME: use BaseMultiToggleGroup once it supports multiple selections with at least one required. -->
                <BaseButton
                    v-for="chip in visibleChips"
                    :key="chip.key"
                    theme="neutral"
                    size="sm"
                    :class="activeFilters.has(chip.key) ? 'bg-toggle-option-selected' : ''"
                    :aria-pressed="activeFilters.has(chip.key)"
                    @click="toggleFilter(chip.key)">
                    {{ chip.label }}
                </BaseButton>
                <span class="hidden hover-capable:inline-flex">
                    <BaseTooltip content-class="max-w-100">
                        <template #trigger>
                            <BaseButton theme="tertiary" size="sm" icon-only :aria-label="t('components.conversationSearch.info')">
                                <i class="bi bi-info-circle" aria-hidden="true"></i>
                            </BaseButton>
                        </template>
                        <template #content>{{ tooltipContent }}</template>
                    </BaseTooltip>
                </span>
                <BaseButton v-if="searchInput" theme="tertiary" size="sm" class="ml-auto" @click="handleClear">{{ t("components.conversationSearch.clear") }}</BaseButton>
            </div>
        </div>
    </BaseCard>

    <!-- Search loading state -->
    <div v-if="isFetching" class="flex flex-col justify-center items-center py-10 gap-3">
        <LoadingSpinner class="text-ink-muted h-8 w-8" :aria-label="t('components.conversationSearch.searching')" />
        <p class="text-ink-muted details">{{ t("components.conversationSearch.searchingEllipsis") }}</p>
    </div>

    <!-- HTTP/network error -->
    <BaseNotification v-else-if="error && hasSearched" theme="error" hide-dismiss :text="t('components.conversationSearch.genericError')" />

    <!-- LLM error (HTTP 200 but search service failed internally) -->
    <BaseNotification v-else-if="hasSearched && metadata?.llm_error" theme="error" hide-dismiss :text="t('components.conversationSearch.unavailable')" />

    <!-- Empty results after search -->
    <div v-else-if="hasSearched && groupedResults.length === 0" class="px-4 md:px-6 py-3 break-words">
        <p class="text-ink-muted details">
            <!-- Spaces live inside interpolations: Vue's whitespace condensing drops a bare space next to a comment or at the start of a fragment. -->
            {{ t("components.conversationSearch.noResults", { query })
            }}<template v-if="searchedDateRange">{{ " " + t("components.conversationSearch.searchedRange", { range: searchedDateRange }) }}</template
            >{{ " " }}
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -- punctuation: separator before the clear button -->
            <span>&middot;</span>{{ " " }}<BaseButton theme="tertiary" size="sm" @click="handleClear">{{ t("components.conversationSearch.clearShort") }}</BaseButton>
        </p>
    </div>

    <!-- Search results -->
    <div v-else-if="hasSearched && groupedResults.length > 0" class="px-4 pt-2 pb-4 md:px-6 md:pt-4">
        <p class="details md:body-small text-ink-muted mb-1 md:mb-1 leading-relaxed">
            {{ t("components.conversationSearch.found", { count: groupedResults.length }, groupedResults.length) }}
            <span>{{ t("components.conversationSearch.matches", { count: results.length }, results.length) }}</span>
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -- punctuation: separator before the date range -->
            <span v-if="searchedDateRange"> &middot; {{ searchedDateRange }}</span>
        </p>
        <p v-if="matchedDateRange" class="details text-ink-muted mb-2 md:mb-3">
            <span class="whitespace-nowrap">{{ t("components.conversationSearch.newest", { date: matchedDateRange.newest }) }}</span>
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -- punctuation: separator between the dates -->
            <span> &middot; </span>
            <span class="whitespace-nowrap">{{ t("components.conversationSearch.oldest", { date: matchedDateRange.oldest }) }}</span>
        </p>
        <div class="space-y-3">
            <div v-for="group in groupedResults" :key="group.session.uuid">
                <!-- Session card: top result preview -->
                <BaseLink :href="group.topResult.session.url" class="block" @click="handleResultClick(group.topResult, 0)">
                    <BaseCard padding="sm" interactive>
                        <div class="flex flex-wrap justify-between items-start gap-1 mb-1">
                            <span class="body-small text-ink-muted flex items-center gap-2">
                                <BaseChip size="2xs">
                                    <i :class="getResultBadge(group.topResult.result_type).icon" class="text-[10px]" aria-hidden="true"></i>
                                    {{ getResultBadge(group.topResult.result_type).label }}
                                </BaseChip>
                                <span v-if="formatRelevance(group.topResult.relevance)">{{ formatRelevance(group.topResult.relevance) }}</span>
                            </span>
                            <span class="details text-ink-muted whitespace-nowrap">{{ formatDate(group.topResult.message.when) }}</span>
                        </div>
                        <h3 class="body-small-semibold text-ink mb-1">{{ group.session.title || t("components.conversationSearch.untitled") }}</h3>
                        <!-- eslint-disable-next-line vue/no-v-html -- content is escaped via escapeHtml before highlight insertion -->
                        <p class="body-small text-ink-muted line-clamp-2" v-html="getSnippetHtml(group.topResult.message.content, group.topResult.matched_phrase)"></p>
                        <span v-if="group.matchCount > 1" class="details text-ink-muted mt-1 inline-block">
                            {{ t("components.conversationSearch.moreMatches", { count: group.matchCount - 1 }, group.matchCount - 1) }}
                        </span>
                    </BaseCard>
                </BaseLink>

                <!-- Expandable additional matches -->
                <div v-if="group.matchCount > 1">
                    <BaseButton
                        theme="tertiary"
                        size="sm"
                        :aria-expanded="expandedSessions.has(group.session.uuid)"
                        class="mt-1 ml-2 md:ml-4"
                        @click="toggleExpanded(group.session.uuid)">
                        {{
                            expandedSessions.has(group.session.uuid)
                                ? t("components.conversationSearch.hideMore", { count: group.matchCount - 1 })
                                : t("components.conversationSearch.showMore", { count: group.matchCount - 1 })
                        }}
                    </BaseButton>
                    <div v-if="expandedSessions.has(group.session.uuid)" class="ml-2 md:ml-4 mt-2 space-y-2 border-l-2 border-border pl-2 md:pl-3">
                        <BaseLink
                            v-for="(item, idx) in group.results.slice(1)"
                            :key="item.message.id"
                            :href="item.session.url"
                            class="block"
                            @click="handleResultClick(item, idx + 1)">
                            <BaseCard padding="sm" interactive>
                                <div class="flex flex-wrap items-center gap-2 mb-1">
                                    <BaseChip size="2xs">
                                        <i :class="getResultBadge(item.result_type).icon" class="text-[10px]" aria-hidden="true"></i>
                                        {{ getResultBadge(item.result_type).label }}
                                    </BaseChip>
                                    <span class="details text-ink-muted whitespace-nowrap">{{ formatDate(item.message.when) }}</span>
                                </div>
                                <!-- eslint-disable-next-line vue/no-v-html -- content is escaped via escapeHtml before highlight insertion -->
                                <p class="body-small text-ink-muted line-clamp-2" v-html="getSnippetHtml(item.message.content, item.matched_phrase)"></p>
                            </BaseCard>
                        </BaseLink>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseChip from "~vue/components/BaseChip.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import { type SearchOptions, type SearchResultItem, type SearchResultType, useConversationSearch } from "~vue/composables/useConversationSearch";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { formatDate as localizedDate } from "~vue/utils/formatDate";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

interface Props {
    documentUploadEnabled?: boolean;
    documentCreatorEnabled?: boolean;
}

const props = defineProps<Props>();

const searchInput = ref("");
const expandedSessions = ref(new Set<string>());
const { query, results, metadata, groupedResults, isFetching, error, hasSearched, search, logResultClicked, reset, hydrate } = useConversationSearch();

const searchedDateRange = computed(() => {
    if (!metadata.value?.date_range_oldest) {
        return null;
    }
    const oldest = formatDateFull(metadata.value.date_range_oldest);
    const newest = metadata.value.date_range_newest ? formatDateFull(metadata.value.date_range_newest) : null;
    if (!newest || oldest === newest) {
        return oldest;
    }
    return `${oldest} – ${newest}`;
});

const matchedDateRange = computed(() => {
    if (results.value.length === 0) {
        return null;
    }
    let oldest = results.value[0].message.when;
    let newest = oldest;
    for (const r of results.value) {
        if (r.message.when < oldest) {
            oldest = r.message.when;
        }
        if (r.message.when > newest) {
            newest = r.message.when;
        }
    }
    return {
        oldest: formatDateFull(oldest),
        newest: formatDateFull(newest),
    };
});

interface FilterChip {
    key: string;
    label: string;
    roles: string[];
    purposes: string[];
    sources: ("v1" | "v2")[];
    alwaysVisible?: boolean;
    flagProp?: keyof Props;
    contentFilter?: string;
}

const allFilterChips = computed((): FilterChip[] => [
    { key: "chat", label: t("components.conversationSearch.chips.chats"), roles: ["user", "assistant"], purposes: ["chat"], sources: ["v1"], alwaysVisible: true },
    {
        key: "uploaded_docs",
        label: t("components.conversationSearch.chips.uploadedDocuments"),
        roles: ["user"],
        purposes: ["file_upload", "pasted_content", "visual_document_analysis"],
        sources: ["v1"],
        flagProp: "documentUploadEnabled",
    },
    {
        key: "created_docs",
        label: t("components.conversationSearch.chips.createdDocuments"),
        roles: ["user", "assistant", "tool"],
        purposes: ["chat"],
        contentFilter: "document_output",
        sources: ["v1"],
        flagProp: "documentCreatorEnabled",
    },
]);

const visibleChips = computed(() => allFilterChips.value.filter((c) => c.alwaysVisible || (c.flagProp && props[c.flagProp])));

const tooltipContent = computed(() => {
    if (props.documentUploadEnabled && props.documentCreatorEnabled) {
        return t("components.conversationSearch.tooltipBoth");
    }
    if (props.documentUploadEnabled) {
        return t("components.conversationSearch.tooltipUploaded");
    }
    if (props.documentCreatorEnabled) {
        return t("components.conversationSearch.tooltipCreated");
    }
    return t("components.conversationSearch.tooltip");
});

const activeFilters = ref(new Set(["chat"]));

function toggleFilter(key: string) {
    const next = new Set(activeFilters.value);
    if (next.has(key)) {
        if (next.size > 1) {
            next.delete(key);
        } else {
            return;
        }
    } else {
        next.add(key);
    }
    activeFilters.value = next;
    if (hasSearched.value && query.value) {
        search(query.value, resolveFilterOptions());
    }
}

function resolveFilterOptions(): SearchOptions {
    if (activeFilters.value.size === 1 && activeFilters.value.has("chat")) {
        return {};
    }
    const roles = new Set<string>();
    const purposes = new Set<string>();
    const sources = new Set<"v1" | "v2">();
    let contentFilter: string | undefined;
    for (const chip of visibleChips.value) {
        if (activeFilters.value.has(chip.key)) {
            chip.roles.forEach((r) => roles.add(r));
            chip.purposes.forEach((p) => purposes.add(p));
            chip.sources?.forEach((s) => sources.add(s));
            if (chip.contentFilter) {
                contentFilter = chip.contentFilter;
            }
        }
    }
    const opts: SearchOptions = { roles: [...roles], purposes: [...purposes] };
    if (sources.size) {
        opts.sources = [...sources];
    }
    if (contentFilter && activeFilters.value.size === 1) {
        opts.content_filter = contentFilter;
    }
    return opts;
}

onMounted(() => {
    if (hydrate()) {
        searchInput.value = query.value;
    }
});

async function handleSearch() {
    const trimmed = searchInput.value.trim();
    if (!trimmed || isFetching.value) {
        return;
    }
    expandedSessions.value.clear();
    await search(trimmed, resolveFilterOptions());
}

async function handleRefresh() {
    if (isFetching.value) {
        return;
    }
    const trimmed = searchInput.value.trim();
    if (!trimmed) {
        return;
    }
    await search(trimmed, resolveFilterOptions());
}

function handleClear() {
    searchInput.value = "";
    expandedSessions.value.clear();
    reset();
}

function toggleExpanded(uuid: string) {
    if (expandedSessions.value.has(uuid)) {
        expandedSessions.value.delete(uuid);
    } else {
        expandedSessions.value.add(uuid);
    }
}

function handleResultClick(item: SearchResultItem, position: number) {
    logResultClicked(item, position);
}

watch(searchInput, (newVal) => {
    if (!newVal && hasSearched.value) {
        reset();
    }
});

function getResultBadge(resultType: SearchResultType) {
    const badges = {
        chat: { label: t("components.conversationSearch.badges.chat"), icon: "bi-chat-dots" },
        uploaded_doc: { label: t("components.conversationSearch.badges.uploadedDoc"), icon: "bi-file-earmark-arrow-up" },
        created_doc: { label: t("components.conversationSearch.badges.createdDoc"), icon: "bi-file-earmark-text" },
        tool_output: { label: t("components.conversationSearch.badges.toolOutput"), icon: "bi-gear" },
    };
    return badges[resultType] || badges.chat;
}

function formatRelevance(relevance: number) {
    if (relevance >= 9) {
        return t("components.conversationSearch.bestMatch");
    }
    if (relevance >= 7) {
        return t("components.conversationSearch.goodMatch");
    }
    if (relevance >= 5) {
        return t("components.conversationSearch.related");
    }
    return "";
}

function extractDocContent(raw: string) {
    // Created docs are stored as Python dict repr: {'document': {'content': '...', 'title': '...'}}
    // or JSON: {"document": {"content": "...", "title": "..."}}
    const patterns = [/['"]content['"]\s*:\s*['"](.+?)['"]\s*[,}]/s, /['"]title['"]\s*:\s*['"](.+?)['"]\s*[,}]/s];
    for (const pattern of patterns) {
        const match = raw.match(pattern);
        if (match) {
            return match[1].replace(/\\n/g, " ").replace(/\\'/g, "'");
        }
    }
    return null;
}

function getSnippetHtml(content: string, matchedPhrase: string) {
    // Clean up raw document blobs before displaying
    if (content.startsWith("{'document':") || content.startsWith('{"document":')) {
        const extracted = extractDocContent(content);
        if (extracted) {
            content = extracted;
        }
    }

    if (!matchedPhrase) {
        const escaped = escapeHtml(content);
        return escaped.length <= 150 ? escaped : escaped.slice(0, 150) + "...";
    }

    const lowerContent = content.toLowerCase();
    const lowerPhrase = matchedPhrase.toLowerCase();
    const index = lowerContent.indexOf(lowerPhrase);

    if (index === -1) {
        const escaped = escapeHtml(content);
        return escaped.length <= 150 ? escaped : escaped.slice(0, 150) + "...";
    }

    const contextChars = 60;
    const start = Math.max(0, index - contextChars);
    const end = Math.min(content.length, index + matchedPhrase.length + contextChars);

    const before = escapeHtml(content.slice(start, index));
    const phrase = escapeHtml(content.slice(index, index + matchedPhrase.length));
    const after = escapeHtml(content.slice(index + matchedPhrase.length, end));

    const prefix = start > 0 ? "..." : "";
    const suffix = end < content.length ? "..." : "";

    return `${prefix}${before}<mark class="bg-surface-sunken text-ink px-1 rounded">${phrase}</mark>${after}${suffix}`;
}

function escapeHtml(text: string) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
}

function formatDateFull(isoDate: string) {
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) {
        return isoDate;
    }
    return localizedDate(date, "date");
}

function formatDate(dateStr: string) {
    try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) {
            return dateStr;
        }
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            return t("components.conversationSearch.today");
        }
        if (diffDays === 1) {
            return t("components.conversationSearch.yesterday");
        }
        if (diffDays < 7) {
            return t("components.conversationSearch.daysAgo", { count: diffDays });
        }

        return localizedDate(date, "date");
    } catch {
        return dateStr;
    }
}

const isSearchActive = computed(() => hasSearched.value);
defineExpose({ hasSearched: isSearchActive });
</script>
